import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Barcode, Search, Printer, Package, Pill, Box, ScanLine, Tag, MapPin, Calendar,
  Filter, FileDown, Eye, Loader2, Check, Edit, RefreshCw, ClipboardCheck, Plus,
  AlertTriangle, ArrowUpDown, CheckCircle, XCircle, Clock, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const SKU = () => {
  const { api, user } = useAuth();
  const scanRef = useRef(null);
  const isMasterAdmin = (user?.perfil || '').toLowerCase() === 'master_admin';

  // State
  const [catalog, setCatalog] = useState({ items: [], resumo: { total: 0, medicamentos: 0, materiais: 0 } });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterPrateleira, setFilterPrateleira] = useState('');

  // Scanner
  const [scanInput, setScanInput] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  // Labels
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [copies, setCopies] = useState(1);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Prateleira edit
  const [editPrat, setEditPrat] = useState(null);
  const [editPratValue, setEditPratValue] = useState('');

  // Inventário state
  const [inventarios, setInventarios] = useState([]);
  const [activeInv, setActiveInv] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [invScanInput, setInvScanInput] = useState('');
  const [invQty, setInvQty] = useState('');
  const [invScanning, setInvScanning] = useState(false);
  const [invScannedItem, setInvScannedItem] = useState(null);

  // Delete inventory state
  const [deleteInvId, setDeleteInvId] = useState(null);
  const [deleteJustificativa, setDeleteJustificativa] = useState('');
  // Fetch catalog
  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (filterTipo) params.set('tipo', filterTipo);
      if (filterPrateleira) params.set('prateleira', filterPrateleira);
      const res = await api.get(`/sku/catalog?${params.toString()}`);
      setCatalog(res.data);
    } catch { toast.error('Erro ao carregar catálogo'); }
    finally { setLoading(false); }
  }, [api, searchTerm, filterTipo, filterPrateleira]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  // Sync SKUs
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/sku/sync');
      toast.success(res.data.message);
      fetchCatalog();
    } catch { toast.error('Erro ao sincronizar SKUs'); }
    finally { setSyncing(false); }
  };

  // Scanner lookup
  const handleScan = async (code) => {
    if (!code?.trim()) return;
    setScanning(true);
    setScanResult(null);
    try {
      const res = await api.get(`/sku/lookup/${encodeURIComponent(code.trim())}`);
      setScanResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Item não encontrado para este código');
      } else {
        toast.error('Erro na busca');
      }
    } finally { setScanning(false); }
  };

  // Scanner input handler - detects barcode reader (fast keystrokes ending with Enter)
  const handleScanKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan(scanInput);
    }
  };

  // Update prateleira
  const handleSavePrateleira = async () => {
    if (!editPrat) return;
    try {
      await api.put(`/sku/item/${editPrat}/prateleira?prateleira=${encodeURIComponent(editPratValue)}`);
      toast.success('Estante atualizada');
      setEditPrat(null);
      fetchCatalog();
    } catch { toast.error('Erro ao atualizar'); }
  };

  // Generate PDF
  const handleGeneratePdf = async (itemIds) => {
    setGeneratingPdf(true);
    try {
      const res = await api.post('/sku/labels/pdf', {
        item_ids: itemIds || Array.from(selectedItems),
        copies: copies,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'etiquetas_sku.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado com sucesso');
    } catch { toast.error('Erro ao gerar PDF'); }
    finally { setGeneratingPdf(false); }
  };

  // Toggle selection
  const toggleSelect = (id) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === catalog.items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(catalog.items.map(i => i.id)));
    }
  };

  const formatDate = (d) => {
    if (!d || d.length < 10) return '-';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR'); }
    catch { return d; }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  // Inventário handlers
  const fetchInventarios = useCallback(async () => {
    try {
      const res = await api.get('/sku/inventarios');
      setInventarios(res.data || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchInventarios(); }, [fetchInventarios]);

  const handleCriarInventario = async () => {
    setInvLoading(true);
    try {
      const res = await api.post('/sku/inventario');
      toast.success('Sessão de inventário criada');
      await fetchInventarios();
      // Load the new session
      const inv = await api.get(`/sku/inventario/${res.data.id}`);
      setActiveInv(inv.data);
    } catch { toast.error('Erro ao criar sessão'); }
    finally { setInvLoading(false); }
  };

  const handleOpenInventario = async (invId) => {
    setInvLoading(true);
    try {
      const res = await api.get(`/sku/inventario/${invId}`);
      setActiveInv(res.data);
    } catch { toast.error('Erro ao carregar sessão'); }
    finally { setInvLoading(false); }
  };

  const handleInvScan = async () => {
    if (!invScanInput.trim()) return;
    setInvScanning(true);
    setInvScannedItem(null);
    try {
      const res = await api.get(`/sku/lookup/${encodeURIComponent(invScanInput.trim())}`);
      setInvScannedItem(res.data);
      setInvQty('');
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Item não encontrado' : 'Erro na busca');
    } finally { setInvScanning(false); }
  };

  const handleRegistrarContagem = async () => {
    if (!activeInv || !invScannedItem || invQty === '') return;
    try {
      await api.post(`/sku/inventario/${activeInv.id}/scan`, {
        item_id: invScannedItem.id,
        quantidade_fisica: parseFloat(invQty),
      });
      toast.success(`${invScannedItem.nome} registrado`);
      // Refresh session
      const res = await api.get(`/sku/inventario/${activeInv.id}`);
      setActiveInv(res.data);
      setInvScannedItem(null);
      setInvScanInput('');
      setInvQty('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar');
    }
  };

  const handleFinalizarInventario = async () => {
    if (!activeInv) return;
    if (!confirm('Finalizar esta sessão de inventário? As quantidades do sistema serão recalculadas.')) return;
    try {
      const res = await api.post(`/sku/inventario/${activeInv.id}/finalizar`);
      toast.success(res.data.message);
      const inv = await api.get(`/sku/inventario/${activeInv.id}`);
      setActiveInv(inv.data);
      fetchInventarios();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao finalizar'); }
  };

  const handleAjustarInventario = async () => {
    if (!activeInv) return;
    if (!confirm('Ajustar o estoque com base neste inventário? Isso alterará as quantidades reais no sistema.')) return;
    try {
      const res = await api.post(`/sku/inventario/${activeInv.id}/ajustar`);
      toast.success(res.data.message);
      const inv = await api.get(`/sku/inventario/${activeInv.id}`);
      setActiveInv(inv.data);
      fetchInventarios();
      fetchCatalog();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao ajustar'); }
  };

  const handleDeleteInventario = async () => {
    if (!deleteInvId || deleteJustificativa.trim().length < 5) return;
    try {
      await api.delete(`/sku/inventario/${deleteInvId}`, { data: { justificativa: deleteJustificativa.trim() } });
      toast.success('Inventário excluído');
      setDeleteInvId(null);
      setDeleteJustificativa('');
      if (activeInv?.id === deleteInvId) setActiveInv(null);
      fetchInventarios();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  return (
    <Layout>
      <div className="space-y-6 p-4 sm:p-6" data-testid="sku-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Barcode className="h-6 w-6 text-primary" />
              Controle SKU
            </h1>
            <p className="text-muted-foreground">Gestão de SKU, etiquetas e leitura por código de barras</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2" onClick={handleSync} disabled={syncing} data-testid="btn-sync-sku">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sincronizar SKUs
            </Button>
            <Button
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleGeneratePdf([])}
              disabled={generatingPdf}
              data-testid="btn-gerar-etiquetas-all"
            >
              {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Gerar Todas Etiquetas
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card><CardContent className="pt-4 text-center">
            <Tag className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold" data-testid="sku-total">{catalog.resumo.total}</p>
            <p className="text-xs text-muted-foreground">SKUs Cadastrados</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-center">
            <Pill className="h-5 w-5 mx-auto mb-1 text-purple-600" />
            <p className="text-2xl font-bold">{catalog.resumo.medicamentos}</p>
            <p className="text-xs text-muted-foreground">Medicamentos</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-center">
            <Box className="h-5 w-5 mx-auto mb-1 text-blue-600" />
            <p className="text-2xl font-bold">{catalog.resumo.materiais}</p>
            <p className="text-xs text-muted-foreground">Materiais</p>
          </CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="scanner" className="space-y-4">
          <TabsList>
            <TabsTrigger value="scanner" data-testid="tab-scanner">
              <ScanLine className="h-4 w-4 mr-2" /> Leitor de Código
            </TabsTrigger>
            <TabsTrigger value="catalogo" data-testid="tab-catalogo">
              <Package className="h-4 w-4 mr-2" /> Catálogo SKU
            </TabsTrigger>
            <TabsTrigger value="etiquetas" data-testid="tab-etiquetas">
              <Printer className="h-4 w-4 mr-2" /> Etiquetas
            </TabsTrigger>
            <TabsTrigger value="inventario" data-testid="tab-inventario">
              <ClipboardCheck className="h-4 w-4 mr-2" /> Inventário
            </TabsTrigger>
          </TabsList>

          {/* Scanner Tab */}
          <TabsContent value="scanner" className="space-y-4">
            <Card className="border-2 border-dashed border-primary/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ScanLine className="h-5 w-5 text-primary" />
                  Leitura de Código de Barras
                </CardTitle>
                <CardDescription>
                  Use o leitor USB ou digite o SKU manualmente. O sistema identifica o item automaticamente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      ref={scanRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyDown={handleScanKeyDown}
                      placeholder="Escaneie o código ou digite o SKU..."
                      className="pl-10 h-12 text-lg font-mono tracking-wider border-2 focus:border-primary"
                      autoFocus
                      data-testid="input-scanner"
                    />
                  </div>
                  <Button
                    onClick={() => handleScan(scanInput)}
                    disabled={scanning || !scanInput.trim()}
                    className="h-12 px-6 gap-2 bg-primary"
                    data-testid="btn-buscar-scan"
                  >
                    {scanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                    Buscar
                  </Button>
                </div>

                {scanResult ? (
                  <div className="p-4 rounded-lg border-2 border-green-300 bg-green-50 dark:bg-green-950/20 space-y-3" data-testid="scan-result">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                          {scanResult.tipo === 'medicamento'
                            ? <Pill className="h-6 w-6 text-purple-600" />
                            : <Box className="h-6 w-6 text-blue-600" />}
                        </div>
                        <div>
                          <p className="font-bold text-lg">{scanResult.nome}</p>
                          <p className="text-sm font-mono text-muted-foreground">{scanResult.sku}</p>
                        </div>
                      </div>
                      <Badge className={`text-sm px-3 py-1 ${
                        scanResult.tipo === 'medicamento' ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-blue-100 text-blue-700 border-blue-200'
                      }`}>
                        {scanResult.tipo === 'medicamento' ? 'Medicamento' : 'Material'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-2 rounded bg-white dark:bg-gray-800 border">
                        <p className="text-xs text-muted-foreground">Estoque Atual</p>
                        <p className="font-bold text-lg">{scanResult.estoque_atual} <span className="text-sm font-normal text-muted-foreground">{scanResult.unidade}</span></p>
                      </div>
                      <div className="p-2 rounded bg-white dark:bg-gray-800 border">
                        <p className="text-xs text-muted-foreground">Lote</p>
                        <p className="font-bold">{scanResult.lote || '-'}</p>
                      </div>
                      <div className="p-2 rounded bg-white dark:bg-gray-800 border">
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" />Validade</p>
                        <p className="font-bold">{formatDate(scanResult.validade)}</p>
                      </div>
                      <div className="p-2 rounded bg-white dark:bg-gray-800 border">
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />Estante</p>
                        <p className="font-bold">{scanResult.prateleira || '-'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => handleGeneratePdf([scanResult.id])}>
                        <Printer className="h-4 w-4" /> Imprimir etiqueta
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <ScanLine className="h-16 w-16 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Aguardando leitura...</p>
                    <p className="text-sm">Aponte o leitor USB para o código de barras do item</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Catalog Tab */}
          <TabsContent value="catalogo" className="space-y-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar por nome, SKU ou estante..."
                      className="pl-9"
                      data-testid="input-search-sku"
                    />
                  </div>
                  <Button
                    variant={filterTipo === 'medicamento' ? 'default' : 'outline'}
                    size="sm"
                    className={`gap-1 ${filterTipo === 'medicamento' ? 'bg-purple-600' : 'border-purple-300 text-purple-700'}`}
                    onClick={() => setFilterTipo(filterTipo === 'medicamento' ? '' : 'medicamento')}
                  >
                    <Pill className="h-4 w-4" /> MED
                  </Button>
                  <Button
                    variant={filterTipo === 'material' ? 'default' : 'outline'}
                    size="sm"
                    className={`gap-1 ${filterTipo === 'material' ? 'bg-blue-600' : 'border-blue-300 text-blue-700'}`}
                    onClick={() => setFilterTipo(filterTipo === 'material' ? '' : 'material')}
                  >
                    <Box className="h-4 w-4" /> MAT
                  </Button>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
              <Card>
                <CardContent className="pt-0 px-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-32">SKU</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead className="w-24">Tipo</TableHead>
                        <TableHead className="w-28">Lote</TableHead>
                        <TableHead className="w-28">Validade</TableHead>
                        <TableHead className="w-28">Estante</TableHead>
                        <TableHead className="w-24 text-right">Estoque</TableHead>
                        <TableHead className="w-20 text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalog.items.map((item) => (
                        <TableRow key={item.id} className="hover:bg-accent/50">
                          <TableCell className="font-mono font-bold text-primary">{item.sku}</TableCell>
                          <TableCell className="font-medium">{item.nome}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${
                              item.tipo === 'medicamento' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'
                            }`}>
                              {item.tipo === 'medicamento' ? 'MED' : 'MAT'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{item.lote || '-'}</TableCell>
                          <TableCell className="text-sm">{formatDate(item.validade)}</TableCell>
                          <TableCell>
                            {item.prateleira ? (
                              <Badge variant="outline" className="text-xs gap-1 cursor-pointer hover:bg-accent"
                                onClick={() => { setEditPrat(item.id); setEditPratValue(item.prateleira || ''); }}>
                                <MapPin className="h-3 w-3" />{item.prateleira}
                              </Badge>
                            ) : (
                              <button
                                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                                onClick={() => { setEditPrat(item.id); setEditPratValue(''); }}
                              >
                                <Edit className="h-3 w-3" /> Definir
                              </button>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {item.estoque_atual} <span className="text-xs font-normal text-muted-foreground">{item.unidade}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => handleGeneratePdf([item.id])}
                              title="Imprimir etiqueta"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {catalog.items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            Nenhum item encontrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Labels Tab */}
          <TabsContent value="etiquetas" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Printer className="h-5 w-5 text-emerald-600" />
                  Geração de Etiquetas
                </CardTitle>
                <CardDescription>
                  Selecione os itens e gere etiquetas em PDF para impressão na Elgin L42 Pro (50x30mm)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">Filtrar por tipo</Label>
                    <div className="flex gap-2">
                      <Button variant={filterTipo === 'medicamento' ? 'default' : 'outline'} size="sm" className={`flex-1 gap-1 ${filterTipo === 'medicamento' ? 'bg-purple-600' : 'border-purple-300 text-purple-700'}`}
                        onClick={() => setFilterTipo(filterTipo === 'medicamento' ? '' : 'medicamento')}>
                        <Pill className="h-4 w-4" /> Medicamentos
                      </Button>
                      <Button variant={filterTipo === 'material' ? 'default' : 'outline'} size="sm" className={`flex-1 gap-1 ${filterTipo === 'material' ? 'bg-blue-600' : 'border-blue-300 text-blue-700'}`}
                        onClick={() => setFilterTipo(filterTipo === 'material' ? '' : 'material')}>
                        <Box className="h-4 w-4" /> Materiais
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Prateleira</Label>
                    <Input
                      value={filterPrateleira}
                      onChange={(e) => setFilterPrateleira(e.target.value)}
                      placeholder="Ex: A1, B2..."
                      data-testid="input-filter-prateleira"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Qtd cópias por etiqueta</Label>
                    <Input type="number" min="1" max="50" value={copies}
                      onChange={(e) => setCopies(parseInt(e.target.value) || 1)}
                      data-testid="input-qtd-etiquetas"
                    />
                  </div>
                </div>

                {/* Item selection */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={selectedItems.size === catalog.items.length && catalog.items.length > 0}
                          onChange={toggleSelectAll} className="rounded" />
                        Selecionar todos
                      </label>
                      <span className="text-xs text-muted-foreground">({selectedItems.size} selecionado{selectedItems.size !== 1 ? 's' : ''})</span>
                    </div>
                    <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                      disabled={selectedItems.size === 0 || generatingPdf}
                      onClick={() => handleGeneratePdf()}
                      data-testid="btn-gerar-etiquetas-selected"
                    >
                      {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                      Gerar PDF ({selectedItems.size})
                    </Button>
                  </div>
                  <div className="divide-y max-h-[400px] overflow-y-auto">
                    {catalog.items.map((item) => (
                      <label key={item.id} className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-accent/50 transition-colors ${
                        selectedItems.has(item.id) ? 'bg-emerald-50 dark:bg-emerald-950/20' : ''
                      }`}>
                        <input type="checkbox" checked={selectedItems.has(item.id)} onChange={() => toggleSelect(item.id)} className="rounded" />
                        <Badge variant="outline" className={`text-xs shrink-0 ${
                          item.tipo === 'medicamento' ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'
                        }`}>{item.tipo === 'medicamento' ? 'MED' : 'MAT'}</Badge>
                        <span className="font-mono text-sm text-primary font-bold shrink-0">{item.sku}</span>
                        <span className="text-sm font-medium flex-1">{item.nome}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {item.lote && `LT: ${item.lote}`} {item.prateleira && `| ${item.prateleira}`}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inventário Tab */}
          <TabsContent value="inventario" className="space-y-4">
            {activeInv ? (
              <>
                {/* Active session header */}
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${
                            activeInv.status === 'em_andamento' ? 'bg-blue-100 text-blue-700' :
                            activeInv.status === 'finalizado' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {activeInv.status === 'em_andamento' ? 'Em andamento' :
                             activeInv.status === 'finalizado' ? 'Finalizado' : 'Ajustado'}
                          </Badge>
                          <span className="text-sm text-muted-foreground">#{activeInv.id.slice(0, 8)}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Operador: {activeInv.operador_nome} | Início: {formatDateTime(activeInv.data_inicio)}
                          {activeInv.data_fim && ` | Fim: ${formatDateTime(activeInv.data_fim)}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setActiveInv(null)} data-testid="btn-voltar-inventarios">
                          Voltar
                        </Button>
                        {activeInv.status === 'em_andamento' && (
                          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 gap-1"
                            onClick={handleFinalizarInventario} data-testid="btn-finalizar-inventario">
                            <Check className="h-4 w-4" /> Finalizar
                          </Button>
                        )}
                        {activeInv.status === 'finalizado' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1"
                            onClick={handleAjustarInventario} data-testid="btn-ajustar-inventario">
                            <ArrowUpDown className="h-4 w-4" /> Ajustar Estoque
                          </Button>
                        )}
                        {isMasterAdmin && (
                          <Button size="sm" variant="outline" className="gap-1 border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => { setDeleteInvId(activeInv.id); setDeleteJustificativa(''); }}
                            data-testid="btn-delete-inv-detail">
                            <Trash2 className="h-4 w-4" /> Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary when finalized */}
                {activeInv.resumo && (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <Card><CardContent className="pt-3 pb-2 text-center">
                      <p className="text-xl font-bold">{activeInv.resumo.total_itens}</p>
                      <p className="text-xs text-muted-foreground">Itens contados</p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-3 pb-2 text-center">
                      <p className="text-xl font-bold text-green-600">{activeInv.resumo.sem_divergencia}</p>
                      <p className="text-xs text-muted-foreground">Sem divergência</p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-3 pb-2 text-center">
                      <p className="text-xl font-bold text-amber-600">{activeInv.resumo.com_divergencia}</p>
                      <p className="text-xs text-muted-foreground">Com divergência</p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-3 pb-2 text-center">
                      <p className="text-xl font-bold text-blue-600">{activeInv.resumo.divergencia_positiva}</p>
                      <p className="text-xs text-muted-foreground">Sobras (+)</p>
                    </CardContent></Card>
                    <Card><CardContent className="pt-3 pb-2 text-center">
                      <p className="text-xl font-bold text-red-600">{activeInv.resumo.divergencia_negativa}</p>
                      <p className="text-xs text-muted-foreground">Faltas (-)</p>
                    </CardContent></Card>
                  </div>
                )}

                {/* Scanner (only when in progress) */}
                {activeInv.status === 'em_andamento' && (
                  <Card className="border-2 border-dashed border-primary/30">
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <ScanLine className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Escanear item para contagem</span>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            value={invScanInput}
                            onChange={(e) => setInvScanInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleInvScan()}
                            placeholder="Escaneie o SKU..."
                            className="pl-9 h-10 font-mono border-2 focus:border-primary"
                            autoFocus
                            data-testid="input-inv-scan"
                          />
                        </div>
                        <Button onClick={handleInvScan} disabled={invScanning || !invScanInput.trim()} className="h-10">
                          {invScanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        </Button>
                      </div>

                      {invScannedItem && (
                        <div className="p-3 rounded-lg border-2 border-blue-300 bg-blue-50 dark:bg-blue-950/20 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-bold">{invScannedItem.nome}</p>
                              <p className="text-xs font-mono text-muted-foreground">{invScannedItem.sku}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Qtd no sistema</p>
                              <p className="font-bold text-lg">{invScannedItem.estoque_atual} <span className="text-xs font-normal">{invScannedItem.unidade}</span></p>
                            </div>
                          </div>
                          <div className="flex items-end gap-3">
                            <div className="flex-1 space-y-1">
                              <Label className="text-sm font-medium">Quantidade física encontrada *</Label>
                              <Input
                                type="number" step="1" min="0"
                                value={invQty}
                                onChange={(e) => setInvQty(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleRegistrarContagem()}
                                placeholder="Quantidade real..."
                                className="h-10 text-lg font-bold"
                                autoFocus
                                data-testid="input-inv-qty"
                              />
                            </div>
                            <Button onClick={handleRegistrarContagem}
                              disabled={invQty === ''}
                              className="h-10 gap-1 bg-blue-600 hover:bg-blue-700"
                              data-testid="btn-registrar-contagem"
                            >
                              <Check className="h-4 w-4" /> Registrar
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Items list */}
                {activeInv.itens.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Itens contados ({activeInv.itens.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 px-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-28">SKU</TableHead>
                            <TableHead>Item</TableHead>
                            <TableHead className="w-24 text-right">Sistema</TableHead>
                            <TableHead className="w-24 text-right">Físico</TableHead>
                            <TableHead className="w-28 text-right">Divergência</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {activeInv.itens.map((it) => (
                            <TableRow key={it.item_id} className={`${
                              it.divergencia > 0 ? 'bg-blue-50/50 dark:bg-blue-950/10' :
                              it.divergencia < 0 ? 'bg-red-50/50 dark:bg-red-950/10' : ''
                            }`}>
                              <TableCell className="font-mono text-primary font-bold">{it.sku}</TableCell>
                              <TableCell>
                                <span className="font-medium">{it.item_nome}</span>
                                {it.prateleira && <span className="text-xs text-muted-foreground ml-2">({it.prateleira})</span>}
                              </TableCell>
                              <TableCell className="text-right">{it.quantidade_sistema} {it.unidade}</TableCell>
                              <TableCell className="text-right font-bold">{it.quantidade_fisica} {it.unidade}</TableCell>
                              <TableCell className="text-right">
                                {it.divergencia === 0 ? (
                                  <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />OK</Badge>
                                ) : it.divergencia > 0 ? (
                                  <Badge className="bg-blue-100 text-blue-700">+{it.divergencia}</Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700">{it.divergencia}</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {activeInv.itens.length === 0 && activeInv.status === 'em_andamento' && (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <ScanLine className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p className="font-medium">Nenhum item contado ainda</p>
                      <p className="text-sm">Escaneie os itens e informe a quantidade encontrada</p>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <>
                {/* Inventory session list */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Sessões de Inventário</h2>
                  <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={handleCriarInventario}
                    disabled={invLoading} data-testid="btn-novo-inventario">
                    {invLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Nova Sessão
                  </Button>
                </div>

                {inventarios.length > 0 ? (
                  <div className="space-y-2">
                    {inventarios.map((inv) => (
                      <Card key={inv.id} className="cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => handleOpenInventario(inv.id)}>
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                                inv.status === 'em_andamento' ? 'bg-blue-100' :
                                inv.status === 'finalizado' ? 'bg-amber-100' : 'bg-green-100'
                              }`}>
                                {inv.status === 'em_andamento' ? <Clock className="h-4 w-4 text-blue-600" /> :
                                 inv.status === 'finalizado' ? <AlertTriangle className="h-4 w-4 text-amber-600" /> :
                                 <CheckCircle className="h-4 w-4 text-green-600" />}
                              </div>
                              <div>
                                <p className="font-medium text-sm">Inventário #{inv.id.slice(0, 8)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {inv.operador_nome} — {formatDateTime(inv.data_inicio)}
                                  {inv.resumo && ` — ${inv.resumo.total_itens} itens, ${inv.resumo.com_divergencia} divergência(s)`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {isMasterAdmin && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDeleteInvId(inv.id); setDeleteJustificativa(''); }}
                                  className="p-1.5 rounded-md hover:bg-red-100 text-red-500 hover:text-red-700 transition-colors"
                                  title="Excluir inventário"
                                  data-testid={`btn-delete-inv-${inv.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                              <Badge variant="outline" className={`${
                                inv.status === 'em_andamento' ? 'border-blue-300 text-blue-700' :
                                inv.status === 'finalizado' ? 'border-amber-300 text-amber-700' :
                                'border-green-300 text-green-700'
                              }`}>
                                {inv.status === 'em_andamento' ? 'Em andamento' :
                                 inv.status === 'finalizado' ? 'Finalizado' : 'Ajustado'}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <ClipboardCheck className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p className="font-medium">Nenhuma sessão de inventário</p>
                      <p className="text-sm">Clique em "Nova Sessão" para iniciar a contagem</p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Prateleira edit dialog */}
        <Dialog open={!!editPrat} onOpenChange={(v) => !v && setEditPrat(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" /> Editar Estante</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label>Localização (ex: A1-03, B2-08)</Label>
              <Input value={editPratValue} onChange={(e) => setEditPratValue(e.target.value)}
                placeholder="Ex: A1-03" autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSavePrateleira()}
                data-testid="input-edit-prateleira"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPrat(null)}>Cancelar</Button>
              <Button onClick={handleSavePrateleira} className="gap-1" data-testid="btn-salvar-prateleira">
                <Check className="h-4 w-4" /> Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete inventory confirmation dialog */}
        <Dialog open={!!deleteInvId} onOpenChange={(v) => { if (!v) { setDeleteInvId(null); setDeleteJustificativa(''); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="h-5 w-5" />
                Excluir Inventário
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-muted-foreground">
                Esta ação é irreversível. Informe a justificativa para exclusão.
              </p>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-red-700">Justificativa *</Label>
                <Textarea
                  value={deleteJustificativa}
                  onChange={(e) => setDeleteJustificativa(e.target.value)}
                  placeholder="Motivo da exclusão do inventário..."
                  rows={3}
                  className="resize-none"
                  data-testid="textarea-delete-inv-justificativa"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteInvId(null); setDeleteJustificativa(''); }}>Cancelar</Button>
              <Button
                onClick={handleDeleteInventario}
                disabled={deleteJustificativa.trim().length < 5}
                className="bg-red-600 hover:bg-red-700 gap-1"
                data-testid="btn-confirmar-delete-inv"
              >
                <Trash2 className="h-4 w-4" /> Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SKU;
